import { useSelector } from "react-redux";
import _ from "lodash";
import { useMemo } from "react";
import type { Locale } from "../types/translations";
import type { TranslationSet } from "../types/translations";
import { CALORIES_KEY } from "../constants/translationKeys";
import { getCurrentLocale, isRTL } from "../utils/general";
import { preferredLocaleSelector } from "../selectors/user";
import { i18n } from "../config/i18n";
import { mappings } from "../constants/numberMappings";

const getTranslatedCalories = (
  translations: TranslationSet,
  locale: Locale,
): string => {
  return translateText(translations, CALORIES_KEY, locale);
};

const getLocalisedNumber = (digits: number | string, locale: Locale): string =>
  translateNumber(digits, locale);

const translateText = (
  translations: TranslationSet,
  key: string[],
  locale: Locale,
): string => {
  const translationKeyPath = _.flatten(key).join(".");
  const keyPathWithLocale = _.join([translationKeyPath, locale], ".");
  const translation = _.get(translations, keyPathWithLocale, "");

  return translation as string;
};

const translateNumber = (
  digits: number | string,
  currentlocale: Locale,
): string => {
  if (!digits && digits !== 0) return "";
  return currentlocale === "ar"
    ? digits
        .toString()
        .split("")
        .map((number) => {
          if (mappings[number as keyof typeof mappings]) {
            return mappings[number as keyof typeof mappings];
          }
          return "";
        })
        .join("")
    : digits.toString();
};

const useTranslateText = (): {
  t: (key: string, options?: any) => string;
  locale: Locale;
  isRTL: boolean;
} => {
  const defaultLocale = getCurrentLocale();
  const selectedLocale = useSelector(preferredLocaleSelector);
  const locale = selectedLocale || defaultLocale;

  return useMemo(
    () => ({
      t: (key: string, options: any = {}) =>
        i18n.t(key, { locale, ...options }),
      locale,
      isRTL: isRTL(locale),
    }),
    [locale],
  );
};

const isTranslationKeyPresent = (locale: Locale, key: string): boolean => {
  const translationKey = _.join([locale, key], ".");
  return _.has(i18n.translations, translationKey);
};

export {
  getTranslatedCalories,
  translateText,
  useTranslateText,
  isTranslationKeyPresent,
  getLocalisedNumber,
  translateNumber,
};
